import type { RouteLocationNormalized } from 'vue-router'
import { getRegistrationEventId } from '@/utils/metaPixel'
import { companionPolicy } from '@/native/companion'

/** Meta Pixel ID — also referenced in docs/META_PIXEL_CAPI.md */
export const META_PIXEL_ID = '2034827840795788'

/** Compiled out of the native build (LH_NATIVE=1), so the store apps never carry the Pixel loader. */
const FBEVENTS_URL = __LH_NATIVE__ ? '' : 'https://connect.facebook.net/en_US/fbevents.js'

let pixelBootstrapped = false
let pageViewTracked = false

export function isPublicLandingRoute(route: RouteLocationNormalized): boolean {
    return route.name === 'home' || route.path === '/'
}

/**
 * Installs the Meta Pixel base script and calls `init` once.
 * Does not send PageView — conversion events on signup and OAuth return
 * need the pixel even when the visitor is no longer on `/`.
 */
function bootstrapMetaPixel(): void {
    if (pixelBootstrapped || typeof window === 'undefined') return
    // Companion app: the Pixel never loads (no fbevents.js, no ATT prompt needed).
    if (__LH_NATIVE__ || !companionPolicy.allowMarketingPixels) return
    if (typeof window.fbq === 'function') {
        pixelBootstrapped = true
        return
    }

    pixelBootstrapped = true

    /* eslint-disable -- Meta's standard bootstrap snippet */
    ;(function (f: Window, b: Document, e: string, v: string) {
        if (f.fbq) return
        const n = (f.fbq = function (...args: unknown[]) {
            if (n.callMethod) {
                n.callMethod(...args)
            } else {
                n.queue!.push(args)
            }
        }) as typeof window.fbq & {
            callMethod?: (...args: unknown[]) => void
            queue: unknown[][]
            loaded: boolean
            version: string
            push: typeof n
        }
        if (!f._fbq) f._fbq = n
        n.push = n
        n.loaded = true
        n.version = '2.0'
        n.queue = []
        const t = b.createElement(e)
        t.async = true
        ;(t as HTMLScriptElement).src = v
        const s = b.getElementsByTagName(e)[0]
        s?.parentNode?.insertBefore(t, s)
    })(window, document, 'script', FBEVENTS_URL)
    /* eslint-enable */

    window.fbq!('init', META_PIXEL_ID)
}

/**
 * Loads the Meta Pixel base script and fires PageView once.
 * Call only for unauthenticated visitors on the marketing landing page.
 */
export function loadMetaPixel(): void {
    if (!companionPolicy.allowMarketingPixels) return
    bootstrapMetaPixel()
    if (pageViewTracked || typeof window.fbq !== 'function') return
    pageViewTracked = true
    window.fbq('track', 'PageView')
}

/**
 * Sends a standard Meta event. Initializes the pixel if the landing
 * PageView loader has not run yet, without recording an extra PageView.
 */
export function trackMetaStandardEvent(
    eventName: string,
    params?: Record<string, unknown>,
    eventId?: string,
): boolean {
    // Companion app: every conversion call is a no-op.
    if (!companionPolicy.allowMarketingPixels) return false
    bootstrapMetaPixel()
    if (typeof window.fbq !== 'function') return false
    try {
        if (eventId) {
            window.fbq('track', eventName, params ?? {}, { eventID: eventId })
        } else if (params) {
            window.fbq('track', eventName, params)
        } else {
            window.fbq('track', eventName)
        }
        return true
    } catch (err) {
        console.error(`fbq ${eventName} track failed`, err)
        return false
    }
}

/** Account created. Deduped per user for this browser session and at Meta. */
export function trackCompleteRegistration(userId: string): void {
    if (!userId || typeof window === 'undefined') return
    if (!companionPolicy.allowMarketingPixels) return
    const storageKey = `lh_meta_reg_${userId}`
    try {
        if (sessionStorage.getItem(storageKey)) return
    } catch {
        // Private mode can block sessionStorage; still send the event.
    }
    const sent = trackMetaStandardEvent(
        'CompleteRegistration',
        { content_name: 'account', status: true },
        getRegistrationEventId(userId),
    )
    if (!sent) return
    try {
        sessionStorage.setItem(storageKey, '1')
    } catch {
        // Ignore quota / private-mode failures.
    }
}

export function syncMetaPixel(options: {
    route: RouteLocationNormalized
    isAuthenticated: boolean
    authReady: boolean
}): void {
    if (!companionPolicy.allowMarketingPixels) return
    const { route, isAuthenticated, authReady } = options
    if (!authReady || isAuthenticated || !isPublicLandingRoute(route)) return
    loadMetaPixel()
}
